import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout, ErrorNotice } from '../components/Layout';
import { api } from '../lib/api';
import type { OcrView } from '../lib/types';
import { useEffect } from 'react';
import { useCapture } from '../context/capture';
import { processingStatusText } from '../lib/processing-status';

export function ProcessingPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { clearCapture } = useCapture();
  useEffect(() => clearCapture(), [clearCapture]);
  const status = useQuery({
    queryKey: ['appointment-status', id],
    queryFn: () => api<{ data: { status: string; ocr: OcrView | null; progress: { totalPages: number; sourcePages: number; completedPages: number; stage: string | null } } }>(`/api/appointments/${id}/status`).then((r) => r.data),
    refetchInterval: (query) => {
      const value = query.state.data?.status;
      return value && !['ocr_queued', 'ocr_processing', 'image_uploaded'].includes(value) ? false : 2_000;
    },
  });
  const current = status.data?.status;
  const progress = status.data?.progress;
  const failed = current === 'ocr_failed';
  const stage = processingStatusText(failed ? 'failed' : progress?.stage);
  const percent = stage.isAiStage ? 95 : Math.round(((progress?.completedPages ?? 0) / Math.max(1, progress?.totalPages ?? 1)) * 85);
  useEffect(() => {
    if (current === 'needs_review') navigate(`/appointments/${id}/review`, { replace: true });
  }, [current, id, navigate]);
  return (
    <Layout title="กำลังอ่านข้อมูล">
      <section className="mx-auto max-w-lg text-center">
        <div className="card py-10">
          {failed
            ? <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-rose-100 text-3xl text-rose-700">!</div>
            : <div className="mx-auto h-16 w-16 animate-spin rounded-full border-4 border-teal-100 border-t-teal-700" />}
          <h2 className="mt-6 text-xl font-bold">{failed ? 'อ่านเอกสารไม่สำเร็จ' : 'กำลังอ่านใบนัด'}</h2>
          <p className="mt-2 text-slate-600">{failed ? 'คุณยังเปิดดูไฟล์ต้นฉบับและกรอกข้อมูลเองได้' : 'งานกำลังทำต่อในเบื้องหลัง คุณออกไปใช้หน้าอื่นแล้วกลับมาดูได้'}</p>
          <p className="mt-3 font-semibold text-teal-800">{stage.detail}</p>
          <p className="mt-1 text-sm font-medium text-slate-600">API ที่กำลังใช้: {stage.api}</p>
          {!failed && <>
            {!stage.isAiStage && <p className="mt-1 text-sm text-slate-500">หน้า {progress?.completedPages ?? 0}/{progress?.totalPages ?? 1}</p>}
            <div className="mx-auto mt-6 h-2 max-w-xs overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-teal-600 transition-all" style={{ width: `${Math.max(5, percent)}%` }} />
            </div>
          </>}
          <button type="button" className="btn-secondary mt-6" onClick={() => navigate('/')}>กลับหน้าหลัก</button>
        </div>
        {status.isError && <div className="mt-4"><ErrorNotice message="ตรวจสอบสถานะไม่สำเร็จ ระบบจะลองใหม่เมื่อคุณโหลดหน้า" /></div>}
        {current === 'ocr_failed' && (
          <div className="mt-4 space-y-3">
            <ErrorNotice message={status.data?.ocr?.errorMessage ?? 'OCR อ่านไม่สำเร็จ แต่คุณยังกรอกข้อมูลเองได้'} />
            <button className="btn-primary w-full" onClick={() => navigate(`/appointments/${id}/review`)}>กรอกข้อมูลเอง</button>
          </div>
        )}
      </section>
    </Layout>
  );
}
